import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { CalendarClock, Play, Pause, Trash2, Plus, Zap, Image as ImageIcon } from "lucide-react";

const CADENCES = ["hourly", "daily", "weekly"];

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [form, setForm] = useState({
    account_id: "",
    cadence: "daily",
    topics: "",
    include_hero_image: true,
    start_immediately: true,
  });

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/schedules"),
        api.get("/affiliate/accounts"),
      ]);
      setSchedules(s.data);
      setAccounts(a.data);
      if (a.data.length && !form.account_id) setForm((f) => ({ ...f, account_id: a.data[0].id }));
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3200); };

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const topics = form.topics.split("\n").map((t) => t.trim()).filter(Boolean);
      const payload = {
        account_id: form.account_id,
        cadence: form.cadence,
        topics,
        include_hero_image: form.include_hero_image,
        start_immediately: form.start_immediately,
      };
      await api.post("/schedules", payload);
      setForm((f) => ({ ...f, topics: "" }));
      setShowForm(false);
      showToast("Schedule created. First article publishes soon.");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleStatus = async (s) => {
    const next = s.status === "active" ? "paused" : "active";
    await api.patch(`/schedules/${s.id}`, { status: next });
    showToast(`Schedule ${next}`);
    load();
  };

  const runNow = async (s) => {
    await api.post(`/schedules/${s.id}/run-now`);
    showToast("Queued to run in the next minute.");
    load();
  };

  const del = async (s) => {
    if (!window.confirm("Delete this schedule?")) return;
    await api.delete(`/schedules/${s.id}`);
    showToast("Deleted");
    load();
  };

  const addTopics = async (s) => {
    const t = window.prompt("Add topics (one per line):");
    if (!t) return;
    const topics_to_add = t.split("\n").map((x) => x.trim()).filter(Boolean);
    await api.patch(`/schedules/${s.id}`, { topics_to_add });
    showToast(`Added ${topics_to_add.length} topics`);
    load();
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-end justify-between border-b border-slate-800 pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Auto-Publish</p>
          <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">Schedules</h1>
          <p className="mt-3 text-neutral-400 max-w-2xl">Queue topics and let the AI publish an article on a cadence — hourly, daily, or weekly.</p>
        </div>
        <button
          data-testid="btn-toggle-sched-form"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-slate-950 px-4 py-2.5 rounded-md text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Cancel" : "New schedule"}
        </button>
      </div>

      {toast && (
        <div data-testid="toast" className="fixed bottom-6 right-6 z-50 bg-sky-400 text-slate-950 px-5 py-3 rounded-md font-medium shadow-2xl">
          {toast}
        </div>
      )}

      {showForm && (
        <form onSubmit={create} className="border border-sky-500/40 rounded-xl p-8 bg-gradient-to-br from-sky-500/5 to-transparent space-y-6">
          <h2 className="font-serif text-2xl text-sky-100">Create schedule</h2>
          {error && <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Account</label>
              <select
                data-testid="select-sched-account"
                required
                value={form.account_id}
                onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none"
              >
                <option value="">Select an account…</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.network.network_name} — {a.niche.niche}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Cadence</label>
              <div className="flex gap-2">
                {CADENCES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`cadence-${c}`}
                    onClick={() => setForm({ ...form, cadence: c })}
                    className={`px-4 py-2 text-xs rounded font-mono uppercase flex-1 ${form.cadence === c ? "bg-sky-400 text-slate-950" : "border border-slate-800 text-neutral-400"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Topic queue (one per line — optional; leave empty to auto-pick from niche keywords)</label>
            <textarea
              data-testid="input-topics"
              value={form.topics}
              onChange={(e) => setForm({ ...form, topics: e.target.value })}
              rows={5}
              placeholder="best jar openers for arthritis&#10;easy grip kitchen tools&#10;electric can opener reviews"
              className="w-full bg-slate-900 border border-slate-800 rounded p-3 text-sky-50 focus:border-sky-400 outline-none font-mono text-sm"
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="chk-hero-image"
                checked={form.include_hero_image}
                onChange={(e) => setForm({ ...form, include_hero_image: e.target.checked })}
                className="accent-sky-400"
              />
              <ImageIcon className="w-4 h-4 text-sky-400" />
              <span className="text-neutral-300">Include AI-generated hero image (Nano Banana)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.start_immediately}
                onChange={(e) => setForm({ ...form, start_immediately: e.target.checked })}
                className="accent-sky-400"
              />
              <Zap className="w-4 h-4 text-sky-400" />
              <span className="text-neutral-300">Start immediately</span>
            </label>
          </div>

          <button
            data-testid="btn-create-schedule"
            type="submit"
            className="w-full py-3 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium rounded-md flex items-center justify-center gap-2"
          >
            <CalendarClock className="w-4 h-4" /> Activate schedule
          </button>
        </form>
      )}

      <div className="space-y-3">
        {schedules.length === 0 && !showForm && (
          <p className="text-neutral-500 text-center py-16">No schedules yet. Create one to have the AI ship content every day without lifting a finger.</p>
        )}
        {schedules.map((s) => {
          const acc = accounts.find((a) => a.id === s.account_id);
          const nextRun = new Date(s.next_run);
          const overdue = nextRun < new Date();
          return (
            <div key={s.id} data-testid={`sched-row-${s.id}`} className="border border-slate-800 rounded-lg p-5 hover:border-sky-400/40 transition">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                      s.status === "active" ? "border-emerald-500/40 text-emerald-400" : "border-slate-700 text-neutral-500"
                    }`}>● {s.status}</span>
                    <span className="font-mono text-[10px] uppercase text-sky-400">{s.cadence}</span>
                    {s.include_hero_image && <span className="font-mono text-[10px] text-sky-300/70 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> hero image</span>}
                  </div>
                  <h3 className="font-serif text-lg text-sky-100">
                    {acc ? `${acc.network.network_name} — ${acc.niche.niche}` : "(account removed)"}
                  </h3>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] font-mono">
                    <div><p className="text-neutral-500 uppercase">Queue</p><p className="text-sky-100">{s.topic_queue?.length ?? 0} topics</p></div>
                    <div><p className="text-neutral-500 uppercase">Runs</p><p className="text-sky-100">{s.runs_completed ?? 0}</p></div>
                    <div><p className="text-neutral-500 uppercase">Next</p><p className={overdue ? "text-emerald-300" : "text-sky-100"}>{overdue ? "any moment now" : nextRun.toLocaleString()}</p></div>
                    <div><p className="text-neutral-500 uppercase">Last</p><p className="text-sky-100">{s.last_run ? new Date(s.last_run).toLocaleString() : "—"}</p></div>
                  </div>
                  {s.last_error && <p className="mt-2 text-[11px] text-red-400 font-mono">Last error: {s.last_error}</p>}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    data-testid={`btn-toggle-${s.id}`}
                    onClick={() => toggleStatus(s)}
                    className="text-xs px-3 py-1.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded flex items-center gap-1"
                  >
                    {s.status === "active" ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Resume</>}
                  </button>
                  <button
                    data-testid={`btn-run-now-${s.id}`}
                    onClick={() => runNow(s)}
                    className="text-xs px-3 py-1.5 border border-sky-400 text-sky-300 hover:bg-sky-400 hover:text-slate-950 rounded flex items-center gap-1"
                  >
                    <Zap className="w-3 h-3" /> Run now
                  </button>
                  <button
                    onClick={() => addTopics(s)}
                    className="text-xs px-3 py-1.5 border border-slate-800 text-neutral-400 hover:border-sky-400/50 rounded flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add topics
                  </button>
                  <button
                    data-testid={`btn-delete-sched-${s.id}`}
                    onClick={() => del(s)}
                    className="text-xs px-3 py-1.5 border border-slate-800 text-neutral-500 hover:text-red-400 hover:border-red-500/40 rounded flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
